import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Scale, ArrowLeft } from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

export default function StarsCompare() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { i18n } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];

    useSeo({
        title: "1-5 Yıldız Karşılaştırma",
        description: "Türkiye otel yıldız standartlarının kategori bazında karşılaştırma tablosu — 1, 2, 3, 4 ve 5 yıldız için zorunlu kriter sayıları.",
        path: "/yildiz-standartlari/karsilastir",
    });

    useEffect(() => {
        api.get("/stars/compare")
            .then(({ data }) => setData(data))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    const tr = (obj) => (obj && (obj[lang] || obj.tr)) || "";

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="stars-compare-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/yildiz-standartlari" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Yıldız Standartları
                    </Link>
                    <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight inline-flex items-center gap-3">
                        <Scale className="w-8 h-8 text-gold" /> Karşılaştırma Tablosu
                    </h1>
                    <p className="mt-3 text-white/75 max-w-2xl">
                        Kategori bazında 1-5 yıldız standartları karşılaştırması — her hücrede toplam ve zorunlu kriter sayısı.
                    </p>
                </div>
            </section>

            <section className="max-w-6xl mx-auto px-6 py-12">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">Yükleniyor...</div>
                ) : data ? (
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md overflow-x-auto" data-testid="stars-compare-table">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-light-bg">
                                    <th className="text-left px-5 py-4 text-xs uppercase tracking-wider text-deep-blue font-bold">Kategori</th>
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <th key={s} className="text-center px-3 py-4">
                                            <div className="font-data text-2xl text-gold">{Array.from({ length: s }).map(() => "★").join("")}</div>
                                            <div className="text-[10px] text-deep-blue/70 uppercase tracking-wider mt-1">{s} Yıldız</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.matrix.map((row, ri) => (
                                    <tr key={ri} className="border-t border-slate-100" data-testid={`stars-compare-row-${ri}`}>
                                        <td className="px-5 py-4 text-sm font-semibold text-dark-navy">{tr(row.category)}</td>
                                        {[1, 2, 3, 4, 5].map((s) => {
                                            const cell = row.stars[String(s)] || { total: 0, mandatory: 0 };
                                            return (
                                                <td key={s} className="text-center px-3 py-4">
                                                    <div className="font-data text-base font-bold text-deep-blue">{cell.total}</div>
                                                    <div className="text-[10px] text-deep-blue/60">{cell.mandatory} zorunlu</div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                                <tr className="border-t border-slate-200 bg-dark-navy/5">
                                    <td className="px-5 py-4 text-sm font-bold text-dark-navy">TOPLAM</td>
                                    {data.summary.map((s) => (
                                        <td key={s.star_level} className="text-center px-3 py-4">
                                            <div className="font-data text-base font-bold text-gold">{s.total_requirements}</div>
                                            <div className="text-[10px] text-deep-blue/70">{s.mandatory_count} zorunlu</div>
                                        </td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ) : null}

                <div className="mt-8 grid sm:grid-cols-2 gap-3">
                    {[1, 2, 3, 4, 5].map((s) => (
                        <Link
                            key={s}
                            to={`/yildiz-standartlari/${s}`}
                            className="inline-flex items-center justify-between bg-white rounded-md border border-slate-100 hover:border-gold/40 px-4 py-3 text-sm font-semibold text-dark-navy hover:bg-light-bg"
                            data-testid={`stars-compare-link-${s}`}
                        >
                            <span>
                                <span className="font-data text-gold mr-2">{Array.from({ length: s }).map(() => "★").join("")}</span>
                                {s} Yıldız Detay
                            </span>
                            <span className="text-teal-700">→</span>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
